import React from 'react';
import { SegmentGroup } from '@ark-ui/react/segment-group';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface SegmentOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SegmentGroupInputProps extends BaseInputProps<string> {
  options: SegmentOption[];
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

/**
 * Atomický segmentový přepínač postavený na Ark UI `SegmentGroup`.
 *
 * Pojmenování skupiny řeší `aria-labelledby` na popisek z `<FormField />`
 * (stejný vzor jako {@link RadioGroupInput}).
 */
export const SegmentGroupInput: React.FC<SegmentGroupInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  labelId,
  describedBy,
  testAttributes,
  options,
  orientation = 'horizontal',
  className,
}) => (
  <SegmentGroup.Root
    ref={ref}
    name={name}
    value={value ?? null}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    orientation={orientation}
    onValueChange={(details) => onChange(details.value ?? '')}
    onBlur={onBlur}
    className={clsx(styles.segmentRoot, className)}
    aria-labelledby={labelId}
    aria-describedby={describedBy}
    {...testAttributes}
  >
    <SegmentGroup.Indicator className={styles.segmentIndicator} />
    {options.map((option) => (
      <SegmentGroup.Item
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        className={styles.segmentItem}
        data-testid={`${testAttributes['data-testid']}-option-${option.value}`}
      >
        <SegmentGroup.ItemText className={styles.segmentItemText}>
          {option.label}
        </SegmentGroup.ItemText>
        <SegmentGroup.ItemControl />
        <SegmentGroup.ItemHiddenInput />
      </SegmentGroup.Item>
    ))}
  </SegmentGroup.Root>
);

SegmentGroupInput.displayName = 'SegmentGroupInput';
